import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import { BottomNav } from "@/components/BottomNav";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { LogoutConfirmDialog, DeleteAccountDialog } from "@/components/ConfirmDialogs";
import { 
  ArrowLeft, User, Mail, Settings, LogOut, Trash2, 
  MessageSquare, Send, ChevronRight, Camera, X, Pencil,
  Home, Droplet, LineChart, MessageCircle, Globe, Search, Key
} from "lucide-react";
import { toast } from "sonner";

const Profile = () => {
  const navigate = useNavigate();
  const { user, token, logout, setUser } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordForm, setChangePasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [contactType, setContactType] = useState("");
  const fileInputRef = useRef(null);
  
  const [contactForm, setContactForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    message: "",
  });
  
  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    diabetes_type: "",
    cuisines: [],
    glucose_unit: "mg/dL",
    profile_photo: null,
    preferred_language: language,
  });

  const [editData, setEditData] = useState({ ...profileData });

  const cuisineOptions = [
    { value: "african", label: t('cuisines.african') },
    { value: "european", label: t('cuisines.european') },
    { value: "asian", label: t('cuisines.asian') },
    { value: "north_american", label: t('cuisines.north_american') },
    { value: "south_american", label: t('cuisines.south_american') },
    { value: "middle_eastern", label: t('cuisines.middle_eastern') },
    { value: "caribbean", label: t('cuisines.caribbean') },
    { value: "mixed", label: t('cuisines.mixed') },
  ];

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadProfile = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      if (response.ok) {
        const data = await response.json();
        const profile = {
          name: data.name || "",
          email: data.email || "",
          diabetes_type: data.diabetes_type || "",
          cuisines: data.diet_region?.split(",").filter(c => c) || [],
          glucose_unit: data.glucose_unit || "mg/dL",
          profile_photo: data.profile_photo || null,
          preferred_language: data.preferred_language || language,
        };
        setProfileData(profile);
        setEditData(profile);
      }
    } catch (error) {
      console.error("Failed to load profile:", error);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error(t('profile.photo_upload_error'));
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('profile.photo_size_error'));
      return;
    }

    setLoading(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result;
        
        // Upload to backend
        const response = await fetch(`${API}/profile/photo`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          
          body: JSON.stringify({ photo: base64 }),
        });

        if (response.ok) {
          const data = await response.json();
          setProfileData(prev => ({ ...prev, profile_photo: data.photo_url }));
          setUser(prev => ({ ...prev, profile_photo: data.photo_url }));
          toast.success("Profile photo updated!");
        } else {
          toast.error("Failed to upload photo");
        }
        setLoading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Photo upload error:", error);
      toast.error("Failed to upload photo");
      setLoading(false);
    }
  };

  const handleRemovePhoto = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/profile/photo`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
        
      });

      if (response.ok) {
        setProfileData(prev => ({ ...prev, profile_photo: null }));
        setUser(prev => ({ ...prev, profile_photo: null }));
        toast.success("Profile photo removed");
      } else {
        toast.error("Failed to remove photo");
      }
    } catch (error) {
      toast.error("Failed to remove photo");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify({
          name: editData.name,
          email: editData.email,
          diabetes_type: editData.diabetes_type,
          diet_region: editData.cuisines.join(","),
          glucose_unit: editData.glucose_unit,
          preferred_language: editData.preferred_language,
        }),
      });

      if (response.ok) {
        setProfileData(editData);
        setUser(prev => ({ ...prev, name: editData.name, email: editData.email }));
        // Update app language if changed
        if (editData.preferred_language !== language) {
          setLanguage(editData.preferred_language);
        }
        toast.success(t('common.success') + "!");
        setShowEditDialog(false);
      } else {
        toast.error(t('common.error'));
      }
    } catch (error) {
      console.error("Profile update error:", error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const toggleCuisine = (value) => {
    setEditData(prev => ({
      ...prev,
      cuisines: prev.cuisines.includes(value)
        ? prev.cuisines.filter(c => c !== value)
        : [...prev.cuisines, value],
    }));
  };

  const handleLogout = async () => {
    setLogoutLoading(true);
    await logout();
    toast.success("Logged out successfully");
    setShowLogoutDialog(false);
    setLogoutLoading(false);
    navigate("/");
  };

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      // Actually delete the account via API
      const response = await fetch(`${API}/user/delete`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        toast.success("Account deleted successfully. We'll miss you!");
        await logout();
        navigate("/");
      } else {
        toast.error("Failed to delete account");
      }
    } catch (error) {
      toast.error("Failed to delete account");
    } finally {
      setDeleteLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (changePasswordForm.newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    
    if (changePasswordForm.newPassword !== changePasswordForm.confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    
    setChangePasswordLoading(true);
    try {
      const response = await fetch(`${API}/auth/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify({
          current_password: changePasswordForm.currentPassword,
          new_password: changePasswordForm.newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("Password changed successfully!");
        setShowChangePasswordDialog(false);
        setChangePasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      } else {
        toast.error(data.detail || "Failed to change password");
      }
    } catch (error) {
      console.error("Change password error:", error);
      toast.error("Failed to change password");
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.message.trim()) {
      toast.error("Please enter a message");
      return;
    }
    
    setLoading(true);
    try {
      toast.success("Message sent! We'll get back to you soon.");
      setContactForm({ ...contactForm, message: "" });
      setShowContactForm(false);
      setContactType("");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  const getDiabetesTypeLabel = (type) => {
    const labels = {
      type1: "Type 1 Diabetes",
      type2: "Type 2 Diabetes",
      prediabetes: "Prediabetes",
      monitoring: "Just monitoring",
    };
    return labels[type] || type || "Not set";
  };

  const getCuisineLabels = (cuisines) => {
    const labels = {
      african: "African",
      european: "European",
      asian: "Asian",
      north_american: "North American",
      south_american: "South American",
      middle_eastern: "Middle Eastern",
      caribbean: "Caribbean",
      mixed: "Mixed / Global",
      west_african: "West African",
    };
    if (!cuisines || cuisines.length === 0) return "Not set";
    return cuisines.map(c => labels[c] || c).join(", ");
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b border-orange-100 bg-background/80 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center gap-4">
          <Link to="/dashboard" className="text-muted-foreground hover:text-secondary transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-heading text-xl font-bold text-secondary">{t('profile.title')}</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Profile Header with Photo */}
        <div className="flex items-center gap-4">
          <div className="relative group">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-primary flex items-center justify-center">
              {profileData.profile_photo ? (
                <img 
                  src={profileData.profile_photo} 
                  alt={profileData.name} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-white font-bold text-4xl font-heading">
                  {profileData.name?.charAt(0)?.toUpperCase() || "U"}
                </span>
              )}
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-secondary text-white flex items-center justify-center shadow-lg hover:bg-secondary/80 transition-colors"
              data-testid="upload-photo-btn"
            >
              <Camera className="w-4 h-4" />
            </button>
            {profileData.profile_photo && (
              <button
                onClick={handleRemovePhoto}
                disabled={loading}
                className="absolute top-0 right-0 w-6 h-6 rounded-full bg-destructive text-white flex items-center justify-center shadow-lg hover:bg-destructive/80 transition-colors opacity-0 group-hover:opacity-100"
                data-testid="remove-photo-btn"
              >
                <X className="w-3 h-3" />
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png"
              onChange={handlePhotoUpload}
              className="hidden"
              data-testid="photo-input"
            />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold text-secondary">{profileData.name}</h2>
            <p className="text-muted-foreground">{profileData.email}</p>
          </div>
        </div>

        {/* Profile Details */}
        <Card className="bg-white rounded-3xl border border-orange-100/50">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg font-semibold text-secondary flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              {t('profile.profile_info')}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setEditData(profileData); setShowEditDialog(true); }}
              className="rounded-full"
              data-testid="edit-profile-btn"
            >
              <Pencil className="w-4 h-4 mr-1" />
              {t('common.edit')}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4">
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground">{t('profile.name')}</span>
                <span className="font-medium text-secondary">{profileData.name}</span>
              </div>
              <Separator />
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground">{t('profile.email')}</span>
                <span className="font-medium text-secondary">{profileData.email}</span>
              </div>
              <Separator />
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground">{t('profile.diabetes_type')}</span>
                <span className="font-medium text-secondary">{getDiabetesTypeLabel(profileData.diabetes_type)}</span>
              </div>
              <Separator />
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground">{t('profile.preferred_cuisines')}</span>
                <span className="font-medium text-secondary text-right max-w-[200px]">
                  {getCuisineLabels(profileData.cuisines)}
                </span>
              </div>
              <Separator />
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground">{t('profile.glucose_unit')}</span>
                <span className="font-medium text-secondary">{profileData.glucose_unit}</span>
              </div>
              <Separator />
              <div className="flex justify-between items-center py-2">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">{t('profile.language')}</span>
                </div>
                <LanguageSwitcher variant="compact" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Support Section */}
        <Card className="bg-white rounded-3xl border border-orange-100/50">
          <CardHeader>
            <CardTitle className="font-heading text-lg font-semibold text-secondary flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              {t('profile.support')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <button
              onClick={() => { setContactType("support"); setShowContactForm(true); }}
              className="w-full flex items-center justify-between p-4 rounded-2xl hover:bg-orange-50 transition-colors"
              data-testid="contact-support-btn"
            >
              <span className="text-secondary font-medium">{t('profile.contact_support')}</span>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>
            <button
              onClick={() => { setContactType("feedback"); setShowContactForm(true); }}
              className="w-full flex items-center justify-between p-4 rounded-2xl hover:bg-orange-50 transition-colors"
              data-testid="send-feedback-btn"
            >
              <span className="text-secondary font-medium">{t('profile.send_feedback')}</span>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>
            <button
              onClick={() => { setContactType("food_error"); setShowContactForm(true); }}
              className="w-full flex items-center justify-between p-4 rounded-2xl hover:bg-orange-50 transition-colors"
              data-testid="report-food-error-btn"
            >
              <span className="text-secondary font-medium">{t('profile.report_food_error')}</span>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>
          </CardContent>
        </Card>

        {/* Actions */}
        <Card className="bg-white rounded-3xl border border-orange-100/50">
          <CardHeader>
            <CardTitle className="font-heading text-lg font-semibold text-secondary flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              {t('profile.account')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <button
              onClick={() => setShowChangePasswordDialog(true)}
              className="w-full flex items-center justify-between p-4 rounded-2xl hover:bg-orange-50 transition-colors"
              data-testid="change-password-btn"
            >
              <span className="text-secondary font-medium flex items-center gap-2">
                <Key className="w-5 h-5" />
                {t('profile.change_password')}
              </span>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>
            
            <button
              onClick={handleLogoutClick}
              className="w-full flex items-center justify-between p-4 rounded-2xl hover:bg-orange-50 transition-colors"
              data-testid="logout-btn"
            >
              <span className="text-secondary font-medium flex items-center gap-2">
                <LogOut className="w-5 h-5" />
                {t('profile.log_out')}
              </span>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>
            
            <button
              onClick={handleDeleteClick}
              className="w-full flex items-center justify-between p-4 rounded-2xl hover:bg-destructive/5 transition-colors"
              data-testid="delete-account-btn"
            >
              <span className="text-destructive font-medium flex items-center gap-2">
                <Trash2 className="w-5 h-5" />
                {t('profile.delete_account')}
              </span>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>
          </CardContent>
        </Card>

        {/* Edit Profile Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-secondary">Edit Profile</DialogTitle>
              <DialogDescription>
                Update your profile information below.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  className="rounded-xl"
                  data-testid="edit-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editData.email}
                  onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                  className="rounded-xl"
                  data-testid="edit-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-diabetes">Diabetes Type</Label>
                <Select
                  value={editData.diabetes_type}
                  onValueChange={(value) => setEditData({ ...editData, diabetes_type: value })}
                >
                  <SelectTrigger className="rounded-xl" data-testid="edit-diabetes-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="type1">Type 1 Diabetes</SelectItem>
                    <SelectItem value="type2">Type 2 Diabetes</SelectItem>
                    <SelectItem value="prediabetes">Prediabetes</SelectItem>
                    <SelectItem value="monitoring">Just monitoring</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Preferred Cuisines</Label>
                <div className="grid grid-cols-2 gap-2">
                  {cuisineOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`flex items-center space-x-2 p-2 rounded-xl cursor-pointer transition-colors ${
                        editData.cuisines.includes(option.value) 
                          ? "bg-primary/10 border border-primary" 
                          : "border border-orange-100 hover:border-primary/30"
                      }`}
                      onClick={() => toggleCuisine(option.value)}
                    >
                      <Checkbox
                        checked={editData.cuisines.includes(option.value)}
                        onCheckedChange={() => toggleCuisine(option.value)}
                        id={`edit-cuisine-${option.value}`}
                      />
                      <Label 
                        htmlFor={`edit-cuisine-${option.value}`}
                        className="text-sm cursor-pointer"
                      >
                        {option.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-glucose-unit">{t('profile.glucose_unit')}</Label>
                <Select
                  value={editData.glucose_unit}
                  onValueChange={(value) => setEditData({ ...editData, glucose_unit: value })}
                >
                  <SelectTrigger className="rounded-xl" data-testid="edit-glucose-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mg/dL">mg/dL</SelectItem>
                    <SelectItem value="mmol/L">mmol/L</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveProfile}
                disabled={loading}
                className="rounded-full bg-primary text-white hover:bg-primary/90"
              >
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Contact Form Dialog */}
        <Dialog open={showContactForm} onOpenChange={setShowContactForm}>
          <DialogContent className="rounded-3xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-secondary">
                {contactType === "support" && "Contact Support"}
                {contactType === "feedback" && "Send Feedback"}
                {contactType === "food_error" && "Report Food Error"}
              </DialogTitle>
              <DialogDescription>
                {contactType === "support" && "We're here to help! Describe your issue below."}
                {contactType === "feedback" && "We'd love to hear your thoughts on PlateMind."}
                {contactType === "food_error" && "Help us improve our food database by reporting errors."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="contact-name">Name</Label>
                <Input
                  id="contact-name"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className="rounded-xl"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-email">Email</Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="rounded-xl"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-message">Message</Label>
                <Textarea
                  id="contact-message"
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  className="rounded-xl min-h-[120px]"
                  placeholder={
                    contactType === "food_error" 
                      ? "Please describe the food error (e.g., incorrect carbs, missing food)..."
                      : "Type your message here..."
                  }
                  required
                />
              </div>
              <DialogFooter className="gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowContactForm(false)}
                  className="rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="rounded-full bg-primary text-white hover:bg-primary/90"
                >
                  {loading ? "Sending..." : "Send Message"}
                  <Send className="w-4 h-4 ml-2" />
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>

      {/* Bottom Navigation */}
      <BottomNav />

      {/* Logout Confirmation Dialog */}
      <LogoutConfirmDialog
        isOpen={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        onConfirm={handleLogout}
        loading={logoutLoading}
      />

      {/* Delete Account Confirmation Dialog */}
      <DeleteAccountDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteAccount}
        loading={deleteLoading}
      />

      {/* Change Password Dialog */}
      <Dialog open={showChangePasswordDialog} onOpenChange={setShowChangePasswordDialog}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-secondary">Change Password</DialogTitle>
            <DialogDescription>
              Enter your current password and choose a new one.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                value={changePasswordForm.currentPassword}
                onChange={(e) => setChangePasswordForm({ ...changePasswordForm, currentPassword: e.target.value })}
                className="rounded-xl"
                required
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={changePasswordForm.newPassword}
                onChange={(e) => setChangePasswordForm({ ...changePasswordForm, newPassword: e.target.value })}
                className="rounded-xl"
                required
                minLength={8}
                data-testid="new-password-input"
              />
              <p className="text-xs text-muted-foreground">Minimum 8 characters</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-new-password">Confirm New Password</Label>
              <Input
                id="confirm-new-password"
                type="password"
                value={changePasswordForm.confirmPassword}
                onChange={(e) => setChangePasswordForm({ ...changePasswordForm, confirmPassword: e.target.value })}
                className="rounded-xl"
                required
                minLength={8}
                data-testid="confirm-new-password-input"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowChangePasswordDialog(false);
                  setChangePasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
                }}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={changePasswordLoading || changePasswordForm.newPassword !== changePasswordForm.confirmPassword || changePasswordForm.newPassword.length < 8}
                className="rounded-full bg-primary text-white hover:bg-primary/90"
                data-testid="submit-change-password-btn"
              >
                {changePasswordLoading ? "Changing..." : "Change Password"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profile;
